import type { ClientMessage, ServerMessage } from "@cowork/shared";

const ENGINE_URL = import.meta.env["VITE_ENGINE_WS_URL"] ?? "ws://localhost:3001";

type MessageHandler = (msg: ServerMessage) => void;
type TokenProvider = () => Promise<string | null | undefined>;

class EngineWebSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<MessageHandler>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private tokenProvider: TokenProvider | null = null;

  connect(tokenProvider?: TokenProvider): void {
    this.intentionallyClosed = false;
    this.tokenProvider = tokenProvider ?? null;
    void this.createConnection();
  }

  disconnect(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }

  private async createConnection(): Promise<void> {
    // Fetch a fresh token on every connection attempt (handles expiry on reconnects)
    const token = this.tokenProvider ? await this.tokenProvider() : null;
    // Browser WebSocket can't set custom headers — pass token as ?token= query param
    const url = token ? `${ENGINE_URL}?token=${encodeURIComponent(token)}` : ENGINE_URL;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      console.log("[ws] connected to engine");
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as ServerMessage;
        for (const handler of this.handlers) {
          handler(msg);
        }
      } catch {
        console.error("[ws] failed to parse message:", event.data);
      }
    };

    this.ws.onclose = () => {
      console.log("[ws] disconnected from engine");
      if (!this.intentionallyClosed) {
        this.reconnectTimer = setTimeout(() => { void this.createConnection(); }, 3000);
      }
    };

    this.ws.onerror = (err) => {
      console.error("[ws] error:", err);
    };
  }

  send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      console.warn("[ws] not connected, dropping message");
    }
  }

  addHandler(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export const engineWS = new EngineWebSocket();
